import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Link, useParams, useRouteMatch } from "react-router-dom";

import MaterialTable from "material-table";
import {
  Box,
  Button,
  FormControl,
  Icon,
  MenuItem,
  Select,
  Tooltip,
  TextField,
  InputLabel,
  Typography,
} from "@material-ui/core";
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../../../utilities/constants";
import { getDeptsRequest } from "../../DepartmentsApiActions";
import { useStyles } from "./styles";
import defaultProfile from "../../../../assets/group.png";
import { clearStore } from "../../DepartmentsActions";
import { debounce } from "../../../../utilities/utilityFunctions";
import ImageLoader from "react-load-image";
import Preloader from "../../../../utilities/components/Preloader/Preloader";

/**
 * function to render organization departments
 * @param history: history from router
 * @returns {JSX.Element}
 * @constructor
 */
const OrganizationDepartments = ({
  history,
  getAllDepts,
  depts,
  orgIdHistory,
  orgNameHistory,
  clearValue,
  orgTypeIdHistory,
}) => {
  const classes = useStyles();
  const params = useParams();
  const match = useRouteMatch();

  const [localFilters, setLocalFilters] = useState({
    name: "",
    desc: "",
    active: "",
  });

  const [apiParams, setApiParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: { name: "", desc: "", active: "" },
    isHideLoader: false,
  });

  const updateApiParams = useCallback(
    debounce((filters) => {
      setApiParams((prev) => ({
        ...prev,
        searchData: filters,
        page: 0,
      }));
    }, 400),
    []
  );

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value;
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
    updateApiParams(updatedFilters);
  };

  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
    setApiParams((prev) => ({
      ...prev,
      searchData: updatedFilters,
      page: 0,
    }));
  };

  useEffect(() => {
    getAllDepts(params.id, apiParams, true);
  }, [params.id, apiParams, getAllDepts]);

  const handleProfileRoute = (id) => {
    clearValue();
    history.push({
      pathname: `${match.path.split(":")[0]}departments/${id}`,
      state: {
        orgIdHistory,
        orgNameHistory,
        orgTypeIdHistory,
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        title: "Name",
        filtering: false,
        cellStyle: { width: "25%" },
        render: ({ imageUrl, name, id }) => (
          <Box
            className={classes.profileContainer}
            onClick={() => handleProfileRoute(id)}
          >
            <Box className={classes.profileInnerContainer}>
              <ImageLoader src={imageUrl || defaultProfile}>
                <img alt={id} className={classes.profileImage} />
                <div className={classes.profileImageError}>{id}</div>
                <Preloader alt={id} className={classes.profileImage} />
              </ImageLoader>
            </Box>
            <Box className={classes.nameContainer}>
              <Typography className={classes.name}>{name}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "desc",
        title: "Description",
        filtering: false,
        cellStyle: { width: "40%" },
      },
      {
        field: "patientCount",
        title: "Patients",
        filtering: false,
        cellStyle: { width: "10%", textAlign: "center" },
        render: ({ patientCount }) => <>{patientCount || "0"}</>,
      },
      {
        field: "active",
        title: "Active Status",
        filtering: false,
        cellStyle: { width: "10%", textAlign: "center" },
        render: ({ active }) =>
          active ? (
            <Tooltip arrow placement="top" title="Active">
              <Icon className={classes.statusActive}>check_circle_outline</Icon>
            </Tooltip>
          ) : (
            <Tooltip arrow placement="top" title="Blocked">
              <Icon className={classes.statusBlocked}>block</Icon>
            </Tooltip>
          ),
      },
    ],
    [classes]
  );

  const FilterComponent = useMemo(() => {
    return (
      <Box
        display="flex"
        gap={3}
        sx={{
          padding: "0 16px",
          flexWrap: "wrap",
          "& .MuiFormControl-root, & .MuiTextField-root": {
            marginRight: 150,
          },
        }}
      >
        <TextField
          label="Search Name"
          variant="outlined"
          size="small"
          value={localFilters.name}
          onChange={handleFilterChange("name")}
        />
        <TextField
          label="Search Description"
          variant="outlined"
          size="small"
          value={localFilters.desc}
          onChange={handleFilterChange("desc")}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={localFilters.active}
            onChange={handleSelectChange("active")}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Blocked</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }, [localFilters]);

  return (
    <Box>
      {/* Add Department Button */}
      <Box className={classes.addButtonContainer}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Icon>add</Icon>}
          disableElevation
          component={Link}
          to={{
            pathname: `${match.path.split(":")[0]}addDepartment`,
            state: { orgIdHistory, orgNameHistory, orgTypeIdHistory },
          }}
          className={classes.addButton}
        >
          Department
        </Button>
      </Box>

      {/* Filters */}
      {FilterComponent}

      {/* Material Table */}
      <MaterialTable
        title="Departments"
        columns={columns}
        data={depts.rows}
        style={{ marginTop: "20px" }}
        options={{
          search: false,
          filtering: false,
          sorting: false,
          draggable: false,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          pageSize: apiParams.rowsPerPage,
          emptyRowsWhenPaging: false,
        }}
        totalCount={depts.count}
        page={apiParams.page}
        onChangeRowsPerPage={(pageSize) =>
          setApiParams((prev) => ({ ...prev, rowsPerPage: pageSize }))
        }
        onChangePage={(page) => setApiParams((prev) => ({ ...prev, page }))}
      />
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    depts: state.departments.depts,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllDepts: (orgId, data, isHideLoader = false) => {
      return dispatch(getDeptsRequest(orgId, data, isHideLoader)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    clearValue: () => {
      return dispatch(clearStore());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrganizationDepartments);
